import { stegaClean } from "@sanity/client/stega";

/**
 * Gets a nested property value from an object using dot notation path.
 * @param obj - The object to traverse
 * @param path - Dot-separated path (e.g., 'someNested.otherProp')
 * @returns The value at the path, or undefined if not found
 */
function getNestedValue(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== "object") return undefined;

  const keys = path.split(".");
  let current: unknown = obj;

  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== "object") {
      return undefined;
    }
    current = (current as Record<string, unknown>)[key];
  }

  return current;
}

/**
 * Cleans stega metadata from multiple module properties at once.
 * Supports nested properties using dot notation (e.g., 'someNested.otherProp').
 *
 * @param module - The module object containing properties to clean
 * @param properties - Array of property paths to clean (supports nested paths with dot notation)
 * @returns Array of cleaned values in the same order as the properties array
 *
 * @example
 * ```ts
 * const [imagePosition, colorTheme, nestedProp] = cleanModuleProps(module, [
 *   'imagePosition',
 *   'colorTheme',
 *   'someNested.otherProp'
 * ]);
 * ```
 */
export function cleanModuleProps<T extends Record<string, unknown>>(
  module: T,
  properties: string[],
): unknown[] {
  return properties.map((path) => {
    const value = getNestedValue(module, path);
    return stegaClean(value);
  });
}
